import type { BriefAttachment, ProjectBrief } from '@dyspatch/core';
import {
  AlertTriangle,
  CalendarClock,
  Clock,
  Flag,
  GitBranch,
  Hash,
  History,
  Paperclip,
  Radio,
  User,
  UserCheck,
} from 'lucide-react';
import { type ComponentType, type ReactNode, useRef, useState } from 'react';
import { PropertyGrid, RepoChip, SourceChip } from '@/components/common/bits';
import { Markdown } from '@/components/common/Markdown';
import { BriefStateChip } from '@/components/common/StatusIcon';
import { Button } from '@/components/ui/button';
import { Surface } from '@/components/ui/section';
import { api } from '@/lib/api';
import { duration, formatBytes, minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { cn } from '@/lib/utils';

/**
 * What was asked for, in two pieces that now live in two different columns.
 *
 * `JobFacts` is the job's scalars - state, who opened it, the repos, the
 * deadline. `BriefDetail` is its prose - the goal, the rules, the files, what
 * the plan says. They were one full-width card, and that was the shape that
 * made the job page a mile long: a 1560px band whose content is a 24-character
 * label beside a six-character value, stacked above another band whose content
 * is an 88ch paragraph. Two thirds of every row was empty.
 *
 * The facts are a narrow list by nature, so they moved to the page's rail
 * (`routes/job.tsx`), where narrow is the point, and the prose kept the wide
 * column it actually fills. Neither changed what it says.
 *
 * The goal and the plan summary are rendered as markdown. Both are prose written
 * for a person - one by whoever filed the job, one by the agent that planned it -
 * and both arrive in markdown, so printing the characters of markdown was the
 * one thing this region could get wrong about its only content.
 */
function Deadline({ iso }: { iso: string }) {
  const due = Date.parse(iso);
  if (Number.isNaN(due)) return <span>{iso}</span>;
  const minutes = Math.round(Math.abs(due - Date.now()) / 60_000);
  const late = due < Date.now();
  return (
    <span className={cn(late && 'text-warn')} title={new Date(due).toLocaleString()}>
      {new Date(due).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}{' '}
      · {late ? `${duration(minutes)} overdue` : `in ${duration(minutes)}`}
    </span>
  );
}

/**
 * The plan's own words, for whoever wants to paste the thing the agent wrote
 * rather than what the renderer made of it: `**Go**` reads as bold Go, and a
 * note elsewhere that wants to quote the plan needs the asterisks back.
 * Rendering the summary as markdown is the fix for issue #16's first two
 * criteria and would have quietly broken its third without this.
 */
function CopyRaw({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const reset = useRef<ReturnType<typeof setTimeout> | null>(null);
  return (
    <Button
      size="xs"
      variant="ghost"
      className="text-fg-3 ml-auto h-auto shrink-0 px-1.5 py-0.5 text-2xs font-normal"
      onClick={() => {
        if (reset.current) clearTimeout(reset.current);
        void navigator.clipboard
          ?.writeText(text)
          .then(() => {
            setCopied(true);
            reset.current = setTimeout(() => setCopied(false), 1500);
          })
          // A refused clipboard (no permission, an insecure origin) is not an
          // error to report: the text is on screen to select by hand.
          .catch(() => undefined);
      }}
    >
      {copied ? 'Copied' : 'Copy raw'}
    </Button>
  );
}

type FactRow = { label: string; value: ReactNode; icon?: ComponentType<{ className?: string }> };

/**
 * The job's scalar facts, gathered into one list (issue #64's successor): who
 * opened it, its state, who approved it, the repos it touches, its deadline and
 * where it came from used to be three separate tellings - the page's own
 * identity line, a "decision" band above the goal, and a label/value table at
 * the foot of the goal card.
 */
export function jobFactRows(brief: ProjectBrief): FactRow[] {
  const rows: FactRow[] = [
    { label: 'State', icon: Flag, value: <BriefStateChip status={brief.status} /> },
    { label: 'Opened by', icon: User, value: brief.submittedBy },
  ];
  if (brief.approvedBy) {
    rows.push({
      label: 'Approved by',
      icon: UserCheck,
      value: (
        <>
          {brief.approvedBy}
          {brief.approvedAt ? (
            <span className="text-fg-3"> · {duration(minutesSince(brief.approvedAt))} ago</span>
          ) : null}
        </>
      ),
    });
  }
  rows.push({
    label: 'Repos',
    icon: GitBranch,
    value: (
      <span className="flex flex-wrap gap-1.5">
        {brief.repos.map((repo) => (
          <RepoChip key={repo} repo={repo} />
        ))}
      </span>
    ),
  });
  if (brief.deadline) {
    rows.push({ label: 'Deadline', icon: CalendarClock, value: <Deadline iso={brief.deadline} /> });
  }
  rows.push({
    label: 'Updated',
    icon: Clock,
    value: `${duration(minutesSince(brief.updatedAt))} ago`,
  });
  if (brief.source) {
    rows.push({ label: 'Source', icon: Radio, value: <SourceChip source={brief.source} /> });
  }
  rows.push({
    label: 'Job',
    icon: Hash,
    value: <span className="font-mono">{shortKey(brief.id)}</span>,
  });
  // A draft that came back from a dead planning run: the reason is the only
  // thing that explains why this job is a draft with a failed step on it.
  if (brief.decomposeError) {
    rows.push({
      label: 'Planning failed',
      icon: AlertTriangle,
      value: <span className="text-bad">{brief.decomposeError}</span>,
    });
  }
  // Every version of the goal, oldest first, once an amendment has made a second
  // one. The current text is the goal below; this is where it came from
  // (docs/JOB-LIFECYCLE.md 2.6).
  if (brief.goalHistory.length) {
    rows.push({
      label: 'Goal history',
      icon: History,
      value: (
        <ol className="space-y-1">
          {brief.goalHistory.map((entry, index) => (
            <li key={`${entry.kind}-${entry.at}`} className="text-xs">
              <span className="text-fg-3">
                {entry.kind === 'original' ? 'original' : 'amended'} by {entry.author} ·{' '}
                {new Date(entry.at).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
              {index < brief.goalHistory.length - 1 ? (
                <p className="text-fg-2 mt-0.5 whitespace-pre-wrap">{entry.goal}</p>
              ) : null}
            </li>
          ))}
        </ol>
      ),
    });
  }

  return rows;
}

/**
 * The facts, as the page's rail draws them: one column, because the rail is
 * 340px and a second column there would put two six-character values beside
 * each other and wrap every one that is longer.
 */
export function JobFacts({ brief }: { brief: ProjectBrief }) {
  return (
    <Surface>
      <PropertyGrid rows={jobFactRows(brief)} columns={1} />
    </Surface>
  );
}

export function BriefDetail({
  brief,
  attachments,
}: {
  brief: ProjectBrief;
  /** The files filed with the job (B-n13f). Read-only here: they are attached
      in the composer, and adding or removing one afterwards is not a verb this
      page offers yet. */
  attachments: BriefAttachment[];
}) {
  /** Only the hub's four raster types reach the preview; a PDF or a .txt stays a row. */
  const imageFiles = attachments.filter((file) => file.mediaType.startsWith('image/'));
  const otherFiles = attachments.filter((file) => !file.mediaType.startsWith('image/'));

  return (
    <Surface>
      <Markdown source={brief.goal} className="max-w-[88ch] px-gutter py-3 text-sm" />

      {brief.constraints.length ? (
        <div className="border-t px-gutter py-3">
          <div className="dy-eyebrow">Rules</div>
          <ul className="text-fg-2 mt-1.5 max-w-[88ch] list-disc space-y-1 pl-4 text-sm">
            {/* Keyed by position as well as text: nothing stops a person filing
                the same rule twice, and this list is read-only, never reordered
                and holds no state of its own. */}
            {brief.constraints.map((constraint, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: see above.
              <li key={`${index}-${constraint}`}>{constraint}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Nothing at all when a job carries no files: an empty region with a
          heading over it says a job is missing something, and most jobs are
          only words. The same rule the rules and the plan summary above follow. */}
      {attachments.length ? (
        <div className="border-t px-gutter py-3">
          <div className="dy-eyebrow">Files</div>
          {/*
           * Pictures are shown, everything else is listed.
           *
           * A screenshot is the common case here and it is attached to be
           * looked at: a filename and a byte count is the one thing it cannot
           * tell you. The hub's allowlist is png, jpeg, gif and webp, with no
           * SVG in it, so nothing reaching this <img> can carry script even in
           * principle. The download route still answers
           * `content-disposition: attachment`, which an <img> ignores and a
           * navigation does not, so opening one in a tab still downloads it
           * rather than rendering somebody's upload in the hub's own origin.
           */}
          {imageFiles.length ? (
            <ul className="mt-2 flex flex-wrap gap-3">
              {imageFiles.map((file) => (
                <li key={file.id} className="w-40">
                  <a
                    href={api.jobFileUrl(file.briefId, file.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    <img
                      src={api.jobFileUrl(file.briefId, file.id)}
                      alt={file.filename}
                      loading="lazy"
                      className="border-line h-28 w-40 rounded border bg-bg-2 object-contain"
                    />
                  </a>
                  <div className="mt-1 truncate text-2xs" title={file.filename}>
                    {file.filename}
                  </div>
                  <div className="text-fg-3 truncate text-2xs" title={file.uploadedBy}>
                    {formatBytes(file.byteSize)} · {file.uploadedBy}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
          {otherFiles.length ? (
            <ul className="mt-1.5 space-y-1 text-sm">
              {otherFiles.map((file) => (
                <li key={file.id} className="flex items-center gap-2">
                  <Paperclip className="text-fg-3 size-3 shrink-0" aria-hidden />
                  <a
                    href={api.jobFileUrl(file.briefId, file.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 truncate underline"
                  >
                    {file.filename}
                  </a>
                  <span className="text-fg-3 shrink-0 text-2xs">
                    {formatBytes(file.byteSize)} · {file.uploadedBy}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {brief.summary ? (
        <div className="border-t px-gutter py-3">
          <div className="flex items-center gap-2">
            <div className="dy-eyebrow">What the plan says</div>
            <CopyRaw text={brief.summary} />
          </div>
          <Markdown source={brief.summary} className="mt-1.5 max-w-[88ch] text-sm" />
        </div>
      ) : null}
    </Surface>
  );
}
